import { useEffect, useState } from "react";
import { pdfArtBoxSchema, type PdfArtBox as ArtBox } from "@proof/core";
import { Button, Control } from "./components";

export function PdfArtBox({
  value,
  onChange,
  width,
  height,
  profile = false,
}: {
  value?: ArtBox;
  onChange: (value: ArtBox | undefined) => void;
  width: number;
  height: number;
  profile?: boolean;
}) {
  const initial =
    typeof value === "object" ? value : { x: 0, y: 0, width, height };
  const [draft, setDraft] = useState(initial);
  useEffect(() => setDraft(initial), [JSON.stringify(initial)]);
  const valid = pdfArtBoxSchema.safeParse(draft).success;
  return (
    <details className="pdf-art-box">
      <summary>PDF artwork bounds</summary>
      <p className="inspector-note">
        ArtBox describes the meaningful artwork area in PDF metadata. It does
        not crop the document.
        {profile
          ? " Profile settings apply to exported pages or imposed sheets."
          : " Output profiles can override this setting."}
      </p>
      <Control label="PDF artwork bounds">
        <select
          value={
            value === undefined
              ? "inherit"
              : typeof value === "object"
                ? "custom"
                : value
          }
          onChange={(event) => {
            const selected = event.target.value;
            onChange(
              selected === "inherit"
                ? undefined
                : selected === "custom"
                  ? { x: 0, y: 0, width, height }
                  : (selected as "media" | "trim" | "bleed"),
            );
          }}
        >
          <option value="inherit">
            {profile ? "Inherit page setting" : "PDF default (media)"}
          </option>
          <option value="media">Media boundary</option>
          <option value="trim">Trim boundary</option>
          <option value="bleed">Bleed boundary</option>
          <option value="custom">Custom rectangle</option>
        </select>
      </Control>
      {typeof value === "object" && (
        <>
          <p className="inspector-note">
            Points from the top-left of finished trim. Bounds must fit within
            the exported media, including when production margins are disabled.
          </p>
          <div className="control-grid">
            {(["x", "y", "width", "height"] as const).map((key) => (
              <Control key={key} label={`Artwork ${key} (pt)`}>
                <input
                  type="number"
                  step="any"
                  min={key === "width" || key === "height" ? 0.001 : -14400}
                  max={14400}
                  value={draft[key]}
                  onChange={(event) =>
                    setDraft({ ...draft, [key]: Number(event.target.value) })
                  }
                />
              </Control>
            ))}
          </div>
          {!valid && (
            <p role="alert">
              Enter finite coordinates and positive dimensions within 14,400
              points.
            </p>
          )}
          <Button disabled={!valid} onClick={() => onChange({ ...draft })}>
            Apply artwork bounds
          </Button>
          <Button onClick={() => setDraft(initial)}>
            Reset artwork bounds
          </Button>
        </>
      )}
    </details>
  );
}
